import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as firebaseCourses from '../../services/firebaseCourses';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { enrollCourses, isCourseEnrolled } from '../../utils/enrollmentStore';
import { getEnrollmentsForUser } from '../../services/firebaseEnrollments';
import { getProgress } from '../../services/firebaseProgress';
import { getReviewsForCourse, getMyReviewForCourse, submitReview } from '../../services/firebaseReviews';
import { awardBadge } from '../../services/firebaseBadges';
import { isPaidCourse, formatCoursePrice } from '../../utils/priceUtils';
import { isTextLesson, isVideoLockedForUser, hasFullCourseAccess } from '../../utils/lessonUtils';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import RelatedCourseCard from '../../components/RelatedCourseCard/RelatedCourseCard';
import { PLACEHOLDER_COURSE_IMAGE } from '../../constants';
import './CourseDetails.css';

export default function CourseDetails() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { addItem: addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrolled, setEnrolled] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [courseProgress, setCourseProgress] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError('');
    Promise.all([
      firebaseCourses.getCourse(courseId),
      firebaseCourses.listRelatedCourses(courseId, 4),
    ])
      .then(([detail, related]) => {
        if (!isMounted) return;
        const nextCourse = detail || null;
        setCourse(nextCourse);
        setRelatedCourses(Array.isArray(related) ? related : []);
        if (nextCourse && user) {
          const localEnrolled = isCourseEnrolled(user.uid, nextCourse.id);
          if (localEnrolled) {
            setEnrolled(true);
            getProgress(user.uid, nextCourse.id).then(({ progressPercent }) => {
              if (isMounted) setCourseProgress(progressPercent);
            }).catch(() => {});
          } else {
            getEnrollmentsForUser(user.uid)
              .then((list) => {
                if (isMounted && list.some((c) => String(c.id) === String(nextCourse.id))) {
                  setEnrolled(true);
                  getProgress(user.uid, nextCourse.id).then(({ progressPercent }) => {
                    if (isMounted) setCourseProgress(progressPercent);
                  }).catch(() => {});
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setError('Could not load course. Please try again.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, [courseId, user]);

  useEffect(() => {
    if (!courseId) return;
    getReviewsForCourse(courseId).then(setReviews).catch(() => setReviews([]));
  }, [courseId]);

  useEffect(() => {
    if (!user?.uid || !courseId) return;
    getMyReviewForCourse(user.uid, courseId).then((r) => {
      setMyReview(r);
      if (r) {
        setReviewRating(r.rating);
        setReviewText(r.review || '');
      }
    }).catch(() => {});
  }, [user?.uid, courseId]);

  const handleSubmitReview = (e) => {
    e.preventDefault();
    if (!user || !course) return;
    setReviewSubmitting(true);
    submitReview(user.uid, user.name, course.id, reviewRating, reviewText)
      .then(() => {
        getReviewsForCourse(course.id).then(setReviews);
        firebaseCourses.getCourse(course.id).then((c) => {
          if (c) setCourse((prev) => (prev ? { ...prev, rating: c.rating, review_count: c.review_count, reviewCount: c.review_count } : null));
        });
        getMyReviewForCourse(user.uid, course.id).then(setMyReview);
        awardBadge(user.uid, 'reviewer').catch(() => {});
      })
      .catch(() => {})
      .finally(() => setReviewSubmitting(false));
  };

  // Return loading/error state first — never access course until it's loaded
  if (loading) {
    return (
      <div className="course-details">
        <div className="course-details-banner-wrap">
          <div className="course-details-banner-overlay">
            <p className="course-details-loading">{error || 'Loading course…'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-details">
        <div className="course-details-banner-wrap">
          <div className="course-details-banner-overlay">
            <p className="course-details-loading">{error || 'Course not found.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const coursePrice = course?.price ?? 'Free';
  const paid = isPaidCourse(coursePrice);
  const coursePriceLabel = 'Free';

  const handleAddToCart = () => {
    addToCart({
      id: course.id,
      title: course.title,
      thumbnail: course.thumbnail || course.banner,
      banner: course.banner,
      price: coursePrice,
      instructor: course.instructor,
    });
    setAddedToCart(true);
  };

  const handleEnroll = () => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: `/course/${courseId}` } });
      return;
    }
    if (paid) {
      navigate(`/course/${courseId}/pay`);
      return;
    }
    if (course) {
      enrollCourses(user.uid, {
        id: course.id,
        title: course.title,
        thumbnail: course.thumbnail || course.banner,
        banner: course.banner,
        level: course.level,
        duration: course.duration,
        instructor: course.instructor,
        price: coursePrice,
      });
    }
    setEnrolled(true);
    navigate(`/course/${courseId}/learn`);
  };

  const handleContinue = () => {
    const curriculum = course.curriculum || [];
    const firstIncomplete = curriculum
      .flatMap((s) => s.lessons || [])
      .find((l) => !l.completed);
    const lessonId = firstIncomplete?.id || curriculum[0]?.lessons?.[0]?.id;
    if (lessonId) {
      const lesson = firstIncomplete || curriculum.flatMap((s) => s.lessons || []).find((l) => l.id === lessonId);
      navigate(`/course/${courseId}/${isTextLesson(lesson) ? 'read' : 'learn'}/${lessonId}`);
    }
  };

  const totalLessons = (course.curriculum || []).reduce((acc, s) => acc + (s.lessons || []).length, 0);
  const curriculumFullAccess = hasFullCourseAccess(course.price, enrolled);

  return (
    <div className="course-details">
      <div className="course-details-banner-wrap">
        <img
          src={course.banner || course.thumbnail || PLACEHOLDER_COURSE_IMAGE}
          alt=""
          className="course-details-banner"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = PLACEHOLDER_COURSE_IMAGE;
          }}
        />
        <div className="course-details-banner-overlay">
          <div className="course-details-header">
            <h1 className="course-details-title">{course.title}</h1>
            <p className="course-details-meta">
              {course.level} · {course.duration} · {totalLessons} lessons
            </p>
            {enrolled && (
              <div className="course-details-progress-wrap">
                <ProgressBar value={courseProgress ?? course.progress ?? 0} height={8} />
                <span className="course-details-progress-text">{courseProgress ?? course.progress ?? 0}% complete</span>
              </div>
            )}
            <div className="course-details-actions">
              {enrolled ? (
                <button type="button" className="course-details-btn course-details-btn-primary" onClick={handleContinue}>
                  Continue learning
                </button>
              ) : (
                <>
                  <button type="button" className="course-details-btn course-details-btn-primary" onClick={handleEnroll}>
                    {paid ? 'Pay & enroll' : 'Enroll now'}
                  </button>
                  <button type="button" className="course-details-btn course-details-btn-outline" onClick={handleAddToCart}>
                    {addedToCart ? 'Added to cart' : 'Add to cart'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="course-details-body">
        <div className="course-details-main">
          <section className="course-details-section">
            <h2 className="course-details-section-title">What you'll learn</h2>
            <ul className="course-details-learn-list">
              {(course.whatYouLearn || course.what_you_learn || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="course-details-section">
            <h2 className="course-details-section-title">Curriculum</h2>
            <div className="course-details-accordion">
              {(course.curriculum || []).map((section) => (
                <details key={section.id} className="course-details-accordion-item">
                  <summary className="course-details-accordion-summary">
                    <span>{section.title}</span>
                    <span className="course-details-accordion-count">{section.lessons.length} lessons</span>
                  </summary>
                  <ul className="course-details-accordion-lessons">
                    {section.lessons.map((lesson) => {
                      const locked = isVideoLockedForUser(lesson, course.price, curriculumFullAccess);
                      const isText = lesson.type === 'text';
                      return (
                        <li key={lesson.id}>
                          <span className="course-details-lesson-icon">
                            {locked ? '🔒' : lesson.completed ? '✓' : '○'}
                          </span>
                          <span className="course-details-lesson-title-text">{lesson.title}</span>
                          <span className="course-details-lesson-meta">
                            <span className={`course-details-lesson-kind ${isText ? 'course-details-lesson-kind-text' : 'course-details-lesson-kind-video'}`}>
                              {isText ? 'Text' : 'Video'}
                            </span>
                            {locked && (
                              <span className="course-details-lesson-locked-badge">Locked until purchase</span>
                            )}
                            <span className="course-details-lesson-duration">{lesson.duration}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </details>
              ))}
            </div>
          </section>

          <section className="course-details-section">
            <h2 className="course-details-section-title">Requirements</h2>
            <ul className="course-details-requirements">
              {(course.requirements || []).map((req, i) => (
                <li key={i}>{req}</li>
              ))}
            </ul>
          </section>

          <section className="course-details-section">
            <h2 className="course-details-section-title">Instructor</h2>
            <div className="course-details-instructor">
              {course.instructor && typeof course.instructor === 'object' && course.instructor.avatar ? (
                <img src={course.instructor.avatar} alt="" className="course-details-instructor-avatar" />
              ) : (
                <div className="course-details-instructor-avatar course-details-instructor-avatar-placeholder">
                  {(course.instructor?.name || course.instructor || 'I')[0]}
                </div>
              )}
              <div>
                <p className="course-details-instructor-name">{course.instructor?.name || course.instructor || 'Instructor'}</p>
                <p className="course-details-instructor-title">{course.instructor?.title || ''}</p>
                <p className="course-details-instructor-bio">{course.instructor?.bio || ''}</p>
              </div>
            </div>
          </section>

          <section className="course-details-section course-details-reviews">
            <h2 className="course-details-section-title">Reviews</h2>
            <div className="course-details-rating-summary">
              <span className="course-details-rating-value">{course.rating != null ? Number(course.rating).toFixed(1) : '—'}</span>
              <span className="course-details-rating-stars">★★★★★</span>
              <span className="course-details-review-count">({(course.review_count ?? course.reviewCount ?? 0).toLocaleString()} reviews)</span>
            </div>
            {enrolled && (
              <form className="course-details-review-form" onSubmit={handleSubmitReview}>
                <h3 className="course-details-review-form-title">{myReview ? 'Edit your review' : 'Write a review'}</h3>
                <label className="course-details-review-label">
                  Rating
                  <select value={reviewRating} onChange={(e) => setReviewRating(Number(e.target.value))} className="course-details-review-select">
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>{n} star{n !== 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </label>
                <label className="course-details-review-label">
                  Your review (optional)
                  <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} className="course-details-review-textarea" rows={3} placeholder="Share your experience..." />
                </label>
                <button type="submit" className="course-details-btn course-details-btn-primary" disabled={reviewSubmitting}>
                  {reviewSubmitting ? 'Saving…' : (myReview ? 'Update review' : 'Submit review')}
                </button>
              </form>
            )}
            <ul className="course-details-reviews-list">
              {reviews.map((r) => (
                <li key={r.id} className="course-details-review-item">
                  <div className="course-details-review-item-head">
                    <span className="course-details-review-item-name">{r.userName}</span>
                    <span className="course-details-review-item-rating">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                  </div>
                  {r.review && <p className="course-details-review-item-text">{r.review}</p>}
                </li>
              ))}
            </ul>
            {reviews.length === 0 && !enrolled && <p className="course-details-reviews-empty">No reviews yet. Enroll and complete the course to leave a review.</p>}
          </section>

          {relatedCourses.length > 0 && (
            <section className="course-details-section course-details-related">
              <h2 className="course-details-section-title">More courses you might like</h2>
              <div className="course-details-related-list">
                {relatedCourses.map((c) => (
                  <RelatedCourseCard key={c.id} course={c} />
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="course-details-sidebar">
          <div className="course-details-card">
            <img
              src={course.thumbnail || course.banner || PLACEHOLDER_COURSE_IMAGE}
              alt=""
              className="course-details-card-img"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = PLACEHOLDER_COURSE_IMAGE;
              }}
            />
            <div className="course-details-card-body">
              <p className="course-details-card-price">{formatCoursePrice(coursePriceLabel)}</p>
              {enrolled ? (
                <>
                  <button type="button" className="course-details-btn course-details-btn-primary course-details-btn-block" onClick={handleContinue}>
                    Continue learning
                  </button>
                  <Link to={`/messages?courseId=${encodeURIComponent(course.id)}`} className="course-details-message-owner">
                    Message course owner
                  </Link>
                </>
              ) : (
                <div className="course-details-card-actions">
                  <button type="button" className="course-details-btn course-details-btn-primary course-details-btn-block" onClick={handleEnroll}>
                    {paid ? 'Pay & enroll' : 'Enroll now'}
                  </button>
                  <button type="button" className="course-details-btn course-details-btn-outline course-details-btn-block" onClick={handleAddToCart}>
                    {addedToCart ? 'Added to cart' : 'Add to cart'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
